import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import api, { absUrl } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Heart, Calendar as CalendarIcon, Users } from "lucide-react";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";

export default function TourDetail() {
  const { t } = useTranslation();
  const { slug } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tour, setTour] = useState(null);
  const [participants, setParticipants] = useState(2);
  const [startDate, setStartDate] = useState(null);
  const [paymentType, setPaymentType] = useState("deposit"); // "deposit" | "full"
  const [inWishlist, setInWishlist] = useState(false);
  const [booking, setBooking] = useState(false);
  const [openCal, setOpenCal] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/tours/${slug}`);
        setTour(r.data);
      } catch {
        toast.error("Tour not found");
      }
      if (user) {
        try {
          const wl = await api.get("/wishlist");
          setInWishlist(!!(wl.data || []).find((t) => t.slug === slug));
        } catch {}
      }
    })();
  }, [slug, user]);

  const toggleWish = async () => {
    if (!user) return navigate("/login");
    const r = await api.post("/wishlist/toggle", { tour_id: tour.tour_id });
    setInWishlist(r.data.in_wishlist);
    toast.success(r.data.in_wishlist ? "Saved to wishlist" : "Removed from wishlist");
  };

  const bookAndPay = async () => {
    if (!user) return navigate("/login");
    if (!startDate) { toast.error(t("tours.pick_date")); return; }
    setBooking(true);
    try {
      const b = await api.post("/bookings", {
        tour_id: tour.tour_id,
        start_date: format(startDate, "yyyy-MM-dd"),
        participants,
        payment_type: paymentType,
      });
      const c = await api.post("/payments/checkout", {
        booking_id: b.data.booking_id,
        origin_url: window.location.origin,
        payment_kind: "initial",
      });
      window.location.href = c.data.url;
    } catch {
      toast.error("Could not start payment");
      setBooking(false);
    }
  };

  if (!tour) {
    return (
      <div className="min-h-screen bg-[#FDFBF7]">
        <Navbar /><div className="pt-40 text-center text-slate-400">{t("common.loading")}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Navbar />
      <main className="pt-28 max-w-7xl mx-auto px-6 md:px-12 pb-24" data-testid="tour-detail">
        <div className="mb-10">
          <div className="t-overline text-seafoam mb-4">{tour.category} · {tour.destination}</div>
          <h1 className="t-display text-slate-900 max-w-4xl"><b>{tour.title}</b></h1>
        </div>

        <div className="grid md:grid-cols-3 gap-5 mb-12">
          <div className="md:col-span-2 aspect-[16/10] rounded-2xl overflow-hidden bg-slate-100 relative paper-tear-card paper-grain">
            <img src={absUrl(tour.cover_image_url)} alt={tour.title} className="w-full h-full object-cover" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-1 gap-5">
            {(tour.images || []).slice(0, 2).map((img, i) => (
              <div key={i} className="aspect-[16/10] rounded-2xl overflow-hidden bg-slate-100 relative paper-tear-strip paper-grain">
                <img src={absUrl(img)} alt="" className="w-full h-full object-cover" />
              </div>
            ))}
            {(!tour.images || tour.images.length === 0) && (
              <div className="bg-white rounded-2xl p-6 flex flex-col justify-center">
                <div className="t-overline text-slate-500">{t("tours.duration")}</div>
                <div className="text-2xl font-light text-slate-900 mt-2">{tour.duration}</div>
              </div>
            )}
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-12">
          <div className="md:col-span-2">
            <h2 className="t-overline text-seafoam mb-5">{t("tours.about")}</h2>
            <p className="text-lg text-slate-700 leading-relaxed whitespace-pre-line">{tour.description}</p>
            <div className="mt-12 grid grid-cols-3 gap-6 max-w-lg">
              <div>
                <div className="t-overline text-slate-500">{t("tours.duration")}</div>
                <div className="text-lg font-medium text-slate-900 mt-1">{tour.duration}</div>
              </div>
              <div>
                <div className="t-overline text-slate-500">{t("tours.max_guests")}</div>
                <div className="text-lg font-medium text-slate-900 mt-1">{tour.max_participants}</div>
              </div>
              <div>
                <div className="t-overline text-slate-500">{t("tours.from")}</div>
                <div className="text-lg font-medium text-slate-900 mt-1">${tour.price_usd.toLocaleString()}</div>
              </div>
            </div>
          </div>

          <aside className="bg-white rounded-2xl p-8 shadow-sm h-fit md:sticky md:top-24">
            <div className="flex items-baseline justify-between">
              <div>
                <div className="t-overline text-slate-500">{t("tours.from")}</div>
                <div className="text-3xl font-light text-slate-900 mt-1"><b>${tour.price_usd.toLocaleString()}</b></div>
              </div>
              <button data-testid="wishlist-btn" onClick={toggleWish} className={`p-3 rounded-full border transition-colors ${inWishlist ? "bg-seafoam text-white border-seafoam" : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"}`}>
                <Heart size={16} fill={inWishlist ? "currentColor" : "none"} strokeWidth={1.5} />
              </button>
            </div>

            <div className="mt-6 space-y-4">
              <div>
                <span className="t-overline text-slate-500 flex items-center gap-2"><CalendarIcon size={12} /> {t("tours.start_date")}</span>
                <Popover open={openCal} onOpenChange={setOpenCal}>
                  <PopoverTrigger asChild>
                    <button
                      data-testid="booking-start-date"
                      className="mt-2 w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-left text-slate-900 hover:border-slate-300 transition-colors"
                    >
                      {startDate ? format(startDate, "EEE, dd MMM yyyy") : (
                        <span className="text-slate-400">{t("tours.pick_date")}</span>
                      )}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="w-auto p-0 bg-white border-slate-100 shadow-xl rounded-2xl"
                    align="start"
                  >
                    <Calendar
                      mode="single"
                      selected={startDate}
                      onSelect={(d) => { setStartDate(d); setOpenCal(false); }}
                      disabled={(d) => d < new Date(new Date().setHours(0,0,0,0))}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <label className="block">
                <span className="t-overline text-slate-500 flex items-center gap-2"><Users size={12} /> {t("tours.travellers")}</span>
                <input
                  data-testid="booking-participants"
                  type="number" min={1} max={tour.max_participants}
                  value={participants}
                  onChange={(e) => setParticipants(parseInt(e.target.value || "1"))}
                  className="mt-2 w-full px-4 py-3 rounded-xl border border-slate-200 bg-white outline-none focus:border-seafoam"
                />
              </label>

              <div className="block">
                <span className="t-overline text-slate-500">{t("tours.payment_option")}</span>
                <div className="mt-2 grid grid-cols-2 gap-2" data-testid="payment-type-toggle">
                  <button
                    type="button"
                    data-testid="pay-type-deposit"
                    onClick={() => setPaymentType("deposit")}
                    className={`px-4 py-3 rounded-xl text-sm transition-colors text-left ${paymentType === "deposit" ? "bg-seafoam text-white" : "bg-white border border-slate-200 text-slate-700"}`}
                  >
                    <div className="font-medium">{t("tours.pay_deposit")}</div>
                    <div className={`text-xs mt-1 ${paymentType === "deposit" ? "opacity-90" : "opacity-60"}`}>20% · ${(tour.price_usd * participants * 0.20).toLocaleString(undefined, {maximumFractionDigits: 2})}</div>
                  </button>
                  <button
                    type="button"
                    data-testid="pay-type-full"
                    onClick={() => setPaymentType("full")}
                    className={`px-4 py-3 rounded-xl text-sm transition-colors text-left ${paymentType === "full" ? "bg-seafoam text-white" : "bg-white border border-slate-200 text-slate-700"}`}
                  >
                    <div className="font-medium">{t("tours.pay_full")}</div>
                    <div className={`text-xs mt-1 ${paymentType === "full" ? "opacity-90" : "opacity-60"}`}>${(tour.price_usd * participants).toLocaleString()}</div>
                  </button>
                </div>
                {paymentType === "deposit" && (
                  <div className="mt-2 text-xs text-slate-500">{t("tours.balance_note")}</div>
                )}
              </div>
            </div>

            <button
              data-testid="book-and-pay"
              onClick={bookAndPay}
              disabled={booking}
              className="mt-6 w-full btn-pill bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50 justify-center"
            >
              {booking
                ? t("tours.redirecting")
                : paymentType === "deposit"
                  ? `${t("tours.reserve")} · $${(tour.price_usd * participants * 0.20).toLocaleString(undefined, {maximumFractionDigits: 2})} ${t("tours.deposit_now")}`
                  : `${t("tours.reserve")} · $${(tour.price_usd * participants).toLocaleString()}`}
            </button>

            <div className="mt-5 flex flex-wrap gap-2">
              <a data-testid="contact-whatsapp" href={`https://wa.me/6281234567890?text=I'm%20interested%20in%20${encodeURIComponent(tour.title)}`} target="_blank" rel="noreferrer" className="text-xs px-4 py-2 rounded-full bg-emerald-50 text-emerald-700">WhatsApp</a>
              <a data-testid="contact-telegram" href="https://t.me/acrossindonesia" target="_blank" rel="noreferrer" className="text-xs px-4 py-2 rounded-full bg-sky-50 text-sky-700">Telegram</a>
            </div>
          </aside>
        </div>
      </main>
      <Footer />
    </div>
  );
}
